import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { buildDestinationQuery, buildHostQuery, applyClientSideFilters, applySorting } from '../utils/queryUtils';
import { SearchFilters, SearchContextType } from '../context/SearchTypes';

export const useSearchResults = (): SearchContextType => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const currentPageParam = searchParams.get('page');
  
  const minPriceParam = searchParams.get('min_price');
  const maxPriceParam = searchParams.get('max_price');
  const costTypeParam = searchParams.get('cost_type');
  
  const initPriceRange = (): [number, number] => {
    const costType = costTypeParam || 'avg_accommodation_cost';
    
    const defaultMax = 
      costType === 'avg_accommodation_cost' ? 200 :
      costType === 'avg_meal_cost' ? 100 :
      costType === 'avg_daily_budget' ? 300 :
      costType === 'taxi_cost_per_km' ? 20 : 200;
    
    const minPrice = minPriceParam ? parseInt(minPriceParam) : 0;
    const maxPrice = maxPriceParam ? parseInt(maxPriceParam) : defaultMax;
    
    return [
      isNaN(minPrice) ? 0 : Math.max(0, minPrice),
      isNaN(maxPrice) ? defaultMax : Math.min(maxPrice, defaultMax)
    ] as [number, number];
  };
  
  const [filters, setFilters] = useState<SearchFilters>({
    climate: searchParams.get('climate') || null,
    hasBeach: searchParams.get('beach') === 'true' ? true : 
              searchParams.get('beach') === 'false' ? false : null,
    safety: searchParams.get('safety') ? searchParams.get('safety') : null,
    region: searchParams.get('region') || null,
    priceRange: initPriceRange(),
    sortBy: searchParams.get('sort') || '',
    costType: costTypeParam || 'avg_accommodation_cost',
  });
  
  const [currentPage, setCurrentPage] = useState<number>(
    currentPageParam ? Math.max(1, parseInt(currentPageParam)) : 1
  );
  
  const ITEMS_PER_PAGE = 9;

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    
    if (filters.climate) {
      newParams.set('climate', filters.climate);
    } else {
      newParams.delete('climate');
    }
    
    if (filters.hasBeach !== null) {
      newParams.set('beach', filters.hasBeach.toString());
    } else {
      newParams.delete('beach');
    }
    
    if (filters.safety) {
      newParams.set('safety', filters.safety.toString());
    } else {
      newParams.delete('safety');
    }
    
    if (filters.region) {
      newParams.set('region', filters.region);
    } else {
      newParams.delete('region');
    }
    
    if (filters.priceRange && Array.isArray(filters.priceRange)) {
      const min = Math.floor(Number(filters.priceRange[0])) || 0;
      const max = Math.ceil(Number(filters.priceRange[1])) || 200;
      
      console.log(`Setting URL params for price range: ${min}-${max}`);
      
      newParams.set('min_price', min.toString());
      newParams.set('max_price', max.toString());
    }
    
    if (filters.sortBy) {
      newParams.set('sort', filters.sortBy);
    } else {
      newParams.delete('sort');
    }
    
    if (filters.costType) {
      newParams.set('cost_type', filters.costType);
    } else {
      newParams.delete('cost_type');
    }
    
    newParams.set('page', currentPage.toString());
    
    if (newParams.toString() !== searchParams.toString()) {
      console.log('Updating URL params:', Object.fromEntries(newParams.entries()));
      setSearchParams(newParams, { replace: true });
    }
  }, [filters, currentPage, setSearchParams]);

  const { 
    data: destinationsData, 
    isLoading: isLoadingDestinations,
    error: destinationsError
  } = useQuery({
    queryKey: ['destinations', searchQuery, filters, currentPage, ITEMS_PER_PAGE],
    queryFn: async () => {
      console.log(`Fetching destinations for page ${currentPage} with query: ${searchQuery}`);
      console.log('Current filters:', JSON.stringify(filters));
      
      const hasPriceFilter = filters.priceRange && 
        (filters.priceRange[0] > 0 || filters.priceRange[1] < 300);
      
      const fetchAll = hasPriceFilter;
      
      const query = buildDestinationQuery(
        supabase, 
        searchQuery, 
        filters, 
        currentPage, 
        ITEMS_PER_PAGE,
        fetchAll
      );
      
      const { data, error, count } = await query;
      
      if (error) {
        console.error('Error fetching destinations:', error);
        throw error;
      }
      
      console.log(`Fetched ${data?.length || 0} destinations. Total count: ${count || 0}`);
      
      let processedData = applyClientSideFilters(data || [], filters);
      processedData = applySorting(processedData, filters.sortBy, filters.costType);
      
      const totalFilteredCount = processedData.length;
      
      if (hasPriceFilter) {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        
        console.log(`Applying client-side pagination: page ${currentPage}, from ${start}, to ${end-1}`);
        console.log(`Total after filtering: ${totalFilteredCount}, showing ${Math.min(ITEMS_PER_PAGE, totalFilteredCount - start)}`);
        
        processedData = processedData.slice(start, end);
      }
      
      return {
        destinations: processedData,
        count: hasPriceFilter ? totalFilteredCount : (count || 0)
      };
    },
  });

  const { 
    data: hostsData, 
    isLoading: isLoadingHosts 
  } = useQuery({
    queryKey: ['hosts', searchQuery, currentPage],
    queryFn: async () => {
      if (!searchQuery) return { hosts: [], count: 0 };
      
      const query = buildHostQuery(supabase, searchQuery, currentPage, ITEMS_PER_PAGE);
      if (!query) return { hosts: [], count: 0 };
      
      const { data, error, count } = await query;
      
      if (error) {
        console.error('Error fetching hosts:', error);
        throw error;
      }
      
      return {
        hosts: data || [],
        count: count || 0
      };
    },
  });

  const handleFilterChange = (filter: string, value: any) => {
    console.log(`Filter change: ${filter} = `, value);
    
    setCurrentPage(1);
    
    setFilters(prev => {
      const newFilters = { ...prev };
      
      switch (filter) {
        case 'climate':
          newFilters.climate = value;
          break;
          
        case 'hasBeach':
          newFilters.hasBeach = value;
          break;
          
        case 'safety':
          newFilters.safety = value;
          break;
          
        case 'region':
          newFilters.region = value;
          break;
          
        case 'priceRange':
          if (Array.isArray(value) && value.length === 2) {
            const min = Number(value[0]);
            const max = Number(value[1]);
            
            if (isNaN(min) || isNaN(max)) {
              console.error('Invalid price range values:', value);
              break;
            }
            
            let cappedMax = max;
            if (newFilters.costType === 'avg_accommodation_cost' && cappedMax > 200) {
              cappedMax = 200;
            } else if (newFilters.costType === 'avg_meal_cost' && cappedMax > 100) {
              cappedMax = 100;
            } else if (newFilters.costType === 'avg_daily_budget' && cappedMax > 300) {
              cappedMax = 300;
            } else if (newFilters.costType === 'taxi_cost_per_km' && cappedMax > 20) {
              cappedMax = 20;
            }
            
            newFilters.priceRange = [min, cappedMax] as [number, number];
            console.log('Setting new price range:', newFilters.priceRange);
          } else {
            console.error('Invalid price range value format:', value);
          }
          break;
          
        case 'sortBy':
          newFilters.sortBy = value;
          break;
          
        case 'costType':
          newFilters.costType = value;
          
          if (value === 'avg_accommodation_cost') {
            newFilters.priceRange = [0, 200];
          } else if (value === 'avg_meal_cost') {
            newFilters.priceRange = [0, 100];
          } else if (value === 'avg_daily_budget') {
            newFilters.priceRange = [0, 300];
          } else if (value === 'taxi_cost_per_km') {
            newFilters.priceRange = [0, 20];
          }
          break;
      }
      
      return newFilters;
    });
  };

  const clearFilters = () => {
    setCurrentPage(1);
    
    setFilters({
      climate: null,
      hasBeach: null,
      safety: null,
      region: null,
      priceRange: [0, 200] as [number, number], 
      sortBy: '',
      costType: 'avg_accommodation_cost',
    });
  };

  const handleSetCurrentPage = (page: number) => {
    console.log(`Setting page to ${page}`);
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const setSearchQuery = (query: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('q', query);
    newParams.set('page', '1');
    setSearchParams(newParams, { replace: true });
  };

  const costRanges: Record<string, [number, number]> = {
    avg_daily_budget: [0, 300],
    avg_accommodation_cost: [0, 200], 
    avg_meal_cost: [0, 100],
    taxi_cost_per_km: [0, 20]
  };

  const fetchResults = async () => {
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return {
    searchQuery,
    setSearchQuery,
    destinations: destinationsData?.destinations || [],
    hosts: hostsData?.hosts || [],
    totalCount: destinationsData?.count || 0,
    loading: isLoadingDestinations || isLoadingHosts,
    currentPage,
    setCurrentPage: handleSetCurrentPage,
    ...filters,
    handleFilterChange,
    clearFilters,
    costRanges,
    fetchResults,
    handleSearch,
  };
};
